import { useRef, useState } from 'react'
import { Html } from '@react-three/drei'

/**
 * FurnitureHandles - シンプルな2Dハンドル
 * - 回転ハンドル（↻）
 * - 高さハンドル（↕）
 * - 横幅ハンドル（← →）
 * - 奥行きハンドル（↑ ↓）
 */
export default function FurnitureHandles({
    furniture,
    onRotate,
    onResizeWidth,
    onResizeDepth,
    onResizeHeight,
    onResizeWithOffset,
    onDragStart,
    onDragEnd
}) {
    const [activeHandle, setActiveHandle] = useState(null)
    const dragStartRef = useRef({ x: 0, y: 0, width: 0, depth: 0, height: 0, rotation: 0, posX: 0, posZ: 0 })
    const isFirstMoveRef = useRef(false) // ドラッグ開始後の最初の移動かどうか

    const width = furniture.width
    const depth = furniture.depth
    const height = furniture.height

    // ポインターダウン
    const handlePointerDown = (e, handleType) => {
        e.stopPropagation()
        e.preventDefault()
        setActiveHandle(handleType)
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: furniture.size?.width || 100,
            depth: furniture.size?.depth || 100,
            height: furniture.size?.height || 100,
            rotation: furniture.rotation || 0,
            posX: furniture.position?.x || 0,
            posZ: furniture.position?.z || 0
        }
        isFirstMoveRef.current = true // ドラッグ開始フラグをセット
        onDragStart?.()
        e.target.setPointerCapture(e.pointerId)
    }

    // ポインター移動
    const handlePointerMove = (e) => {
        if (!activeHandle) return
        e.stopPropagation()

        const dx = e.clientX - dragStartRef.current.x
        const dy = e.clientY - dragStartRef.current.y
        const sensitivity = 1.5

        // 初回のみ履歴保存し、2回目以降は上書き
        const overwrite = !isFirstMoveRef.current

        if (activeHandle === 'rotate') {
            // 円周方向の回転（マウスを時計回りに動かすと時計回り）
            // dx: 右に動かすと+回転、dy: 下に動かすと+回転の組み合わせ
            const angleDelta = (dx - dy) * 0.3
            let newAngle = dragStartRef.current.rotation + angleDelta
            // 5度刻みにスナップ
            newAngle = Math.round(newAngle / 5) * 5
            // -180〜180の範囲にクランプ（ループではなく制限）
            newAngle = Math.max(-180, Math.min(180, newAngle))
            onRotate?.(newAngle, overwrite) // overwriteフラグを渡す
        } else if (activeHandle === 'height') {
            // 高さ調整（上にドラッグで増加）
            let newHeight = dragStartRef.current.height - dy * sensitivity
            newHeight = Math.max(20, Math.min(300, Math.round(newHeight)))
            onResizeHeight?.(newHeight, overwrite) // overwriteフラグを渡す
        } else if (activeHandle === 'left' || activeHandle === 'right') {
            // 横幅のみ変更（片側固定）
            const sign = activeHandle === 'right' ? 1 : -1
            const deltaWidth = sign * dx * sensitivity
            let newWidth = dragStartRef.current.width + deltaWidth
            newWidth = Math.max(20, Math.min(300, Math.round(newWidth)))
            const actualDelta = newWidth - dragStartRef.current.width
            // 位置オフセット: 初期位置からの絶対オフセットを計算
            const positionOffset = (actualDelta / 2) * sign
            if (onResizeWithOffset) {
                onResizeWithOffset('width', newWidth, 'x', positionOffset, dragStartRef.current.posX, dragStartRef.current.posZ, overwrite) // overwriteフラグを渡す
            } else {
                onResizeWidth?.(newWidth, overwrite) // overwriteフラグを渡す
            }
        } else if (activeHandle === 'front' || activeHandle === 'back') {
            // 奥行きのみ変更（片側固定）
            const sign = activeHandle === 'front' ? 1 : -1
            const deltaDepth = sign * dy * sensitivity
            let newDepth = dragStartRef.current.depth + deltaDepth
            newDepth = Math.max(20, Math.min(300, Math.round(newDepth)))
            const actualDelta = newDepth - dragStartRef.current.depth
            // 位置オフセット: 初期位置からの絶対オフセットを計算
            const positionOffset = (actualDelta / 2) * sign
            if (onResizeWithOffset) {
                onResizeWithOffset('depth', newDepth, 'z', positionOffset, dragStartRef.current.posX, dragStartRef.current.posZ, overwrite) // overwriteフラグを渡す
            } else {
                onResizeDepth?.(newDepth, overwrite) // overwriteフラグを渡す
            }
        }

        // 初回移動完了
        isFirstMoveRef.current = false
    }

    // ポインターアップ
    const handlePointerUp = (e) => {
        if (activeHandle) {
            e.target.releasePointerCapture(e.pointerId)
            setActiveHandle(null)
            onDragEnd?.()
        }
    }

    const getHandleEvents = (handleType) => ({
        onPointerDown: (e) => handlePointerDown(e, handleType),
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
    })

    // 共通スタイル
    const baseStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        touchAction: 'none',
        border: '2px solid white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        fontWeight: 'bold',
        color: '#000',
    }

    const sideStyle = {
        ...baseStyle,
        width: 28,
        height: 20,
        borderRadius: 4,
        fontSize: '12px',
    }

    const topStyle = {
        ...baseStyle,
        width: 28,
        height: 28,
        borderRadius: '50%',
        fontSize: '16px',
    }

    return (
        <>
            {/* 回転ハンドル（上部左） */}
            <Html position={[-0.15, height + 0.25, 0]} center>
                <div
                    style={{
                        ...topStyle,
                        background: activeHandle === 'rotate' ? '#16a34a' : '#22c55e',
                        cursor: 'grab',
                    }}
                    {...getHandleEvents('rotate')}
                >
                    ↻
                </div>
            </Html>

            {/* 高さハンドル（上部右） */}
            <Html position={[0.15, height + 0.25, 0]} center>
                <div
                    style={{
                        ...topStyle,
                        background: activeHandle === 'height' ? '#7c3aed' : '#a78bfa',
                        cursor: 'ns-resize',
                    }}
                    {...getHandleEvents('height')}
                >
                    ↕
                </div>
            </Html>

            {/* === 側面ハンドル（横幅・奥行き） === */}
            {/* 右 → */}
            <Html position={[width / 2 + 0.12, height / 2, 0]} center>
                <div
                    style={{
                        ...sideStyle,
                        background: activeHandle === 'right' ? '#2563eb' : '#60a5fa',
                        cursor: 'ew-resize',
                    }}
                    {...getHandleEvents('right')}
                >
                    →
                </div>
            </Html>

            {/* 左 ← */}
            <Html position={[-width / 2 - 0.12, height / 2, 0]} center>
                <div
                    style={{
                        ...sideStyle,
                        background: activeHandle === 'left' ? '#2563eb' : '#60a5fa',
                        cursor: 'ew-resize',
                    }}
                    {...getHandleEvents('left')}
                >
                    ←
                </div>
            </Html>

            {/* 前 ↓ */}
            <Html position={[0, height / 2, depth / 2 + 0.12]} center>
                <div
                    style={{
                        ...sideStyle,
                        background: activeHandle === 'front' ? '#2563eb' : '#60a5fa',
                        cursor: 'ns-resize',
                    }}
                    {...getHandleEvents('front')}
                >
                    ↓
                </div>
            </Html>

            {/* 後 ↑ */}
            <Html position={[0, height / 2, -depth / 2 - 0.12]} center>
                <div
                    style={{
                        ...sideStyle,
                        background: activeHandle === 'back' ? '#2563eb' : '#60a5fa',
                        cursor: 'ns-resize',
                    }}
                    {...getHandleEvents('back')}
                >
                    ↑
                </div>
            </Html>

            {/* === ドラッグ中の寸法表示 === */}
            {/* 横幅 - 手前の辺に表示 */}
            {(activeHandle === 'left' || activeHandle === 'right') && (
                <Html position={[0, 0.02, depth / 2 + 0.15]} center>
                    <div style={{
                        background: 'rgba(37, 99, 235, 0.9)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        whiteSpace: 'nowrap',
                    }}>
                        {furniture.size?.width || 100} cm
                    </div>
                </Html>
            )}

            {/* 奥行き - 右辺に表示 */}
            {(activeHandle === 'front' || activeHandle === 'back') && (
                <Html position={[width / 2 + 0.15, 0.02, 0]} center>
                    <div style={{
                        background: 'rgba(37, 99, 235, 0.9)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        whiteSpace: 'nowrap',
                    }}>
                        {furniture.size?.depth || 100} cm
                    </div>
                </Html>
            )}

            {/* 高さ - 左ハンドルの外側に表示 */}
            {activeHandle === 'height' && (
                <Html position={[-width / 2 - 0.4, height / 2, 0]} center>
                    <div style={{
                        background: 'rgba(124, 58, 237, 0.9)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        whiteSpace: 'nowrap',
                    }}>
                        {furniture.size?.height || 100} cm
                    </div>
                </Html>
            )}

            {/* 回転 - 上部に表示 */}
            {activeHandle === 'rotate' && (
                <Html position={[0, height + 0.5, 0]} center>
                    <div style={{
                        background: 'rgba(22, 163, 74, 0.9)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        whiteSpace: 'nowrap',
                    }}>
                        {furniture.rotation || 0}°
                    </div>
                </Html>
            )}
        </>
    )
}
